import { useMemo, useState, type ComponentType } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Chip, FormControlLabel, IconButton, MenuItem, Paper, Stack, Switch, Table, TableBody, TableCell,
  TableContainer, TableFooter, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { AnimatedNumber } from '@/components/AnimatedNumber';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { useRozliczeniaMiesiac } from '@/lib/useAnalytics';
import { formatCount, formatGrosze, formatStawka, odmiana } from '@/lib/format';
import { MIESIACE, etykietaMiesiaca, kwotaDoSchowka, przesunMiesiac, type MiesiacRozliczen } from '@/lib/rozliczenia/miesiac';
import type { PowodBrakuKwot, RozliczenieMiesiaca, SwiezoscRozliczen } from '@/lib/api/analytics';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * RESTAURACJE → ROZLICZENIA MIESIĘCZNE — tabela do wystawiania faktur TuSzamy (OWNER 2026-10-07)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * TYLKO ODCZYT. Miesiąc liczy serwer jako SUMĘ rozliczeń tygodniowych (ten sam silnik, parytet
 * z Main), z tygodni przypisanych do miesiąca: tydzień pon–pt należy do miesiąca, w którym leży
 * więcej jego dni roboczych. Kwoty faktury do GROSZA (bez pełnych złotych tygodniowej wypłaty):
 *   marża netto = Σ członków (obrót × marża), VAT 23% marży netto, FV brutto = netto + VAT.
 * Najważniejsze do przepisania na fakturę: OBRÓT MIESIĄCA i FV TUSZAMA BRUTTO (z kopiowaniem).
 */

const TYDZIEN = ['tydzień rozliczeniowy', 'tygodnie rozliczeniowe', 'tygodni rozliczeniowych'] as const;
const ROZLICZENIE = ['rozliczenie', 'rozliczenia', 'rozliczeń'] as const;
const PIERWSZY_ROK = 2025;

const POWODY: Record<PowodBrakuKwot, string> = {
  AMOUNT_NOT_PROVABLE: 'W co najmniej jednym tygodniu miesiąca są pozycje bez udowodnionej kwoty pobranej — kwoty faktury są wstrzymane, zamiast liczyć je z ceny katalogowej.',
  MARGIN_UNKNOWN: 'Restauracji nie ma w konfiguracji rozliczeń (miasto → marża) — stawka nie jest zgadywana.',
};

const liczby = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

function dzienMiesiac(data: string): string {
  const [, m, d] = data.split('-');
  return `${d}.${m}`;
}

function miesiacZAdresu(p: URLSearchParams): MiesiacRozliczen | undefined {
  const rok = Number(p.get('mrok'));
  const miesiac = Number(p.get('miesiac'));
  if (!Number.isInteger(rok) || !Number.isInteger(miesiac) || rok < PIERWSZY_ROK || rok > 2100 || miesiac < 1 || miesiac > 12) return undefined;
  return { rok, miesiac };
}

function KopiujKwote({ grosze, opis }: { grosze: number | null; opis: string }) {
  const [skopiowano, setSkopiowano] = useState(false);
  if (grosze === null) return null;
  const kopiuj = async () => {
    try {
      await navigator.clipboard.writeText(kwotaDoSchowka(grosze));
      setSkopiowano(true);
      window.setTimeout(() => setSkopiowano(false), 1500);
    } catch {
      setSkopiowano(false);
    }
  };
  return (
    <Tooltip title={skopiowano ? 'Skopiowano' : `Kopiuj ${opis}: ${kwotaDoSchowka(grosze)}`}>
      <IconButton size="small" aria-label={`Kopiuj ${opis}`} onClick={kopiuj} sx={{ ml: 0.25, p: 0.25 }}>
        <ContentCopyIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );
}

function WyborMiesiaca({ wybrany, ustaw, domyslny }: { wybrany: MiesiacRozliczen; ustaw: (m: MiesiacRozliczen | undefined) => void; domyslny: boolean }) {
  const lata = useMemo(() => {
    const doRoku = new Date().getFullYear() + 1;
    return Array.from({ length: doRoku - PIERWSZY_ROK + 1 }, (_, i) => PIERWSZY_ROK + i);
  }, []);
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
      <IconButton aria-label="Poprzedni miesiąc" onClick={() => ustaw(przesunMiesiac(wybrany, -1))} size="small"><ChevronLeftIcon /></IconButton>
      <Typography sx={{ fontWeight: 700, minWidth: 150, textAlign: 'center' }} data-miesiac-etykieta>{etykietaMiesiaca(wybrany)}</Typography>
      <IconButton aria-label="Następny miesiąc" onClick={() => ustaw(przesunMiesiac(wybrany, 1))} size="small"><ChevronRightIcon /></IconButton>
      <TextField select size="small" label="Miesiąc" value={wybrany.miesiac} sx={{ minWidth: 140 }}
        onChange={(e) => ustaw({ rok: wybrany.rok, miesiac: Number(e.target.value) })}>
        {MIESIACE.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
      </TextField>
      <TextField select size="small" label="Rok" value={wybrany.rok} sx={{ minWidth: 100 }}
        onChange={(e) => ustaw({ rok: Number(e.target.value), miesiac: wybrany.miesiac })}>
        {lata.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
      </TextField>
      {!domyslny && <Chip size="small" variant="outlined" label="Ostatni zamknięty" onClick={() => ustaw(undefined)} />}
    </Stack>
  );
}

function Kafel({ etykieta, grosze, wyrozniony = false, niepelne = false }: { etykieta: string; grosze: number | null; wyrozniony?: boolean; niepelne?: boolean }) {
  return (
    <Paper variant="outlined" sx={{ px: 2, py: 1.25, minWidth: 190, flex: '1 1 190px', bgcolor: wyrozniony ? 'action.selected' : undefined }} data-kafel={etykieta}>
      <Typography className="sygnal-liczba" sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1.15, ...liczby }}>
        {niepelne
          ? <Tooltip title="Suma ukryta: co najmniej jedno rozliczenie z obrotem nie ma kwot — byłaby niepełna."><span>—</span></Tooltip>
          : <AnimatedNumber value={grosze} format={formatGrosze} />}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{etykieta}</Typography>
    </Paper>
  );
}

function stawkaWiersza(s: RozliczenieMiesiaca): string {
  if (s.marginNetRate !== null) return formatStawka(s.marginNetRate);
  return s.marginsNet.length ? s.marginsNet.map(formatStawka).join(' / ') : '—';
}

function Kwota({ s, grosze }: { s: RozliczenieMiesiaca; grosze: number | null }) {
  if (s.amountsUnavailableReason) {
    return (
      <Tooltip title={POWODY[s.amountsUnavailableReason]}>
        <Chip size="small" variant="outlined" color="warning" label={s.amountsUnavailableReason === 'AMOUNT_NOT_PROVABLE' ? 'kwota niepotwierdzona' : 'brak stawki'} />
      </Tooltip>
    );
  }
  return <>{formatGrosze(grosze)}</>;
}

function WierszMiesiaca({ s }: { s: RozliczenieMiesiaca }) {
  const wyroznienie = { bgcolor: 'action.selected', fontWeight: 700 } as const;
  return (
    <TableRow hover data-rozliczenie={s.settlementGroupId}>
      <TableCell sx={{ minWidth: { xs: 150, sm: 220 } }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.settlementGroupName}</Typography>
        {s.isGroup && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            {s.members.map((c) => c.name).join(' · ')}
          </Typography>
        )}
      </TableCell>
      <TableCell>{s.cities.join(', ') || '—'}</TableCell>
      <TableCell align="right" sx={liczby}>{s.weeksWithTurnover} z {s.weeksTotal}</TableCell>
      <TableCell align="right" sx={{ ...liczby, ...wyroznienie }} data-obrot>
        {formatGrosze(s.turnoverGrosze)}
        <KopiujKwote grosze={s.turnoverGrosze} opis={`obrót — ${s.settlementGroupName}`} />
      </TableCell>
      <TableCell align="right" sx={liczby}>{stawkaWiersza(s)}</TableCell>
      <TableCell align="right" sx={liczby}><Kwota s={s} grosze={s.marginNetGrosze} /></TableCell>
      <TableCell align="right" sx={liczby}><Kwota s={s} grosze={s.vatGrosze} /></TableCell>
      <TableCell align="right" sx={{ ...liczby, ...wyroznienie }} data-fv-brutto>
        <Kwota s={s} grosze={s.invoiceGrossGrosze} />
        {!s.amountsUnavailableReason && <KopiujKwote grosze={s.invoiceGrossGrosze} opis={`FV brutto — ${s.settlementGroupName}`} />}
      </TableCell>
    </TableRow>
  );
}

export function RozliczeniaMiesieczne({ PanelSwiezosci }: { PanelSwiezosci: ComponentType<{ f: SwiezoscRozliczen }> }) {
  const [parametry, ustawParametry] = useSearchParams();
  const wybrany = useMemo(() => miesiacZAdresu(parametry), [parametry]);
  const { data, isLoading, isFetching, error } = useRozliczeniaMiesiac(wybrany);
  const [pokazZerowe, setPokazZerowe] = useState(false);

  const ustaw = (m: MiesiacRozliczen | undefined) => {
    const p = new URLSearchParams(parametry);
    if (m) { p.set('mrok', String(m.rok)); p.set('miesiac', String(m.miesiac)); } else { p.delete('mrok'); p.delete('miesiac'); }
    ustawParametry(p, { replace: false });
  };

  const okres: MiesiacRozliczen | undefined = wybrany ?? (data ? { rok: data.period.year, miesiac: data.period.month } : undefined);
  const [zObrotem, zerowe] = useMemo(() => {
    const lista = data?.settlements ?? [];
    return [lista.filter((s) => s.turnoverGrosze !== 0), lista.filter((s) => s.turnoverGrosze === 0)];
  }, [data]);
  const widoczne = pokazZerowe ? [...zObrotem, ...zerowe] : zObrotem;
  const t = data?.totals;
  const niepelne = Boolean(t && t.invoiceGrossGrosze === null);
  const tygodnie = data?.period.weeks ?? [];

  return (
    <Stack spacing={2}>
      <NaglowekEkranu
        tytul="Rozliczenia miesięczne"
        opis={okres
          ? `${etykietaMiesiaca(okres)}${data ? ` · ${formatCount(tygodnie.length)} ${odmiana(tygodnie.length, TYDZIEN)}` : ''}`
          : 'Miesiąc…'}
        akcja={okres ? <WyborMiesiaca wybrany={okres} ustaw={ustaw} domyslny={!wybrany} /> : undefined}
      />

      {isLoading && <LoadingState label="Sumowanie rozliczeń tygodniowych miesiąca…" />}
      {error ? <ErrorState error={error} context="Restauracje → Rozliczenia miesięczne." /> : null}

      {data && t && (
        <>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }} data-tygodnie-miesiaca>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {etykietaMiesiaca({ rok: data.period.year, miesiac: data.period.month })} — {formatCount(tygodnie.length)} {odmiana(tygodnie.length, TYDZIEN)}:
            </Typography>
            {tygodnie.map((w) => (
              <Tooltip key={`${w.isoYear}-${w.isoWeek}`} title={`Tydzień ISO ${w.isoWeek}/${w.isoYear} · ${w.daysInMonth} z 5 dni pon–pt w tym miesiącu`}>
                <Chip size="small" variant="outlined" label={`${dzienMiesiac(w.monday)}–${dzienMiesiac(w.friday)}`} data-tydzien={w.monday} />
              </Tooltip>
            ))}
          </Stack>

          <PanelSwiezosci f={data.freshness} />
          {!data.period.monthClosed && (
            <Alert severity="info">
              <strong>Miesiąc rozliczeniowy jeszcze trwa</strong> — ostatni tydzień kończy się w piątek {tygodnie.length ? dzienMiesiac(tygodnie[tygodnie.length - 1]!.friday) : ''}; kwoty mogą się zmienić.
            </Alert>
          )}
          {t.settlementsWithoutAmounts > 0 && (
            <Alert severity="warning">
              <strong>{formatCount(t.settlementsWithoutAmounts)} {odmiana(t.settlementsWithoutAmounts, ROZLICZENIE)} z obrotem bez kwot faktury</strong> (oznaczone w tabeli).
              Sumy marży, VAT i FV są ukryte, bo byłyby niepełne.
            </Alert>
          )}

          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
            <Kafel etykieta="Obrót miesiąca — wszystkie restauracje" grosze={t.turnoverGrosze} wyrozniony />
            <Kafel etykieta="Marża TuSzama netto" grosze={t.marginNetGrosze} niepelne={niepelne} />
            <Kafel etykieta={`VAT ${formatStawka(data.rules.commissionVatRate)}`} grosze={t.vatGrosze} niepelne={niepelne} />
            <Kafel etykieta="FV TuSzama brutto" grosze={t.invoiceGrossGrosze} wyrozniony niepelne={niepelne} />
          </Stack>

          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {formatCount(zObrotem.length)} {odmiana(zObrotem.length, ROZLICZENIE)} z obrotem w miesiącu
              {zerowe.length > 0 && !pokazZerowe ? ` · ukryte bez obrotu: ${formatCount(zerowe.length)}` : ''}
            </Typography>
            {zerowe.length > 0 && (
              <FormControlLabel control={<Switch size="small" checked={pokazZerowe} onChange={(e) => setPokazZerowe(e.target.checked)} />}
                label={`Pokaż zerowe (${formatCount(zerowe.length)})`} />
            )}
          </Stack>

          {widoczne.length === 0 ? (
            <EmptyState title="Brak obrotu w tym miesiącu" description="Żadna restauracja ani grupa nie ma obrotu w tygodniach tego miesiąca rozliczeniowego." />
          ) : (
            <Karta>
              <TableContainer sx={{ maxHeight: '72vh', overflow: 'auto', opacity: isFetching ? 0.6 : 1 }}>
                <Table stickyHeader size="small" aria-label={`Rozliczenia miesięczne — ${etykietaMiesiaca({ rok: data.period.year, miesiac: data.period.month })}`}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Restauracja / grupa</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Miasto</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Tygodnie</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'action.selected' }}>Obrót miesiąca</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Marża %</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Marża TuSzama netto</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>VAT {formatStawka(data.rules.commissionVatRate)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'action.selected' }}>FV TuSzama brutto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {widoczne.map((s) => <WierszMiesiaca key={s.settlementGroupId} s={s} />)}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Razem ({formatCount(zObrotem.length)})</TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell align="right" sx={{ ...liczby, fontWeight: 700, color: 'text.primary', bgcolor: 'action.selected' }}>{formatGrosze(t.turnoverGrosze)}</TableCell>
                      <TableCell />
                      <TableCell align="right" sx={{ ...liczby, fontWeight: 700, color: 'text.primary' }}>{formatGrosze(t.marginNetGrosze)}</TableCell>
                      <TableCell align="right" sx={{ ...liczby, fontWeight: 700, color: 'text.primary' }}>{formatGrosze(t.vatGrosze)}</TableCell>
                      <TableCell align="right" sx={{ ...liczby, fontWeight: 700, color: 'text.primary', bgcolor: 'action.selected' }}>{formatGrosze(t.invoiceGrossGrosze)}</TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </Karta>
          )}

          <Typography variant="caption" component="div" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            Miesiąc rozliczeniowy = pełne tygodnie pon–pt; tydzień należy do miesiąca, w którym leży <strong>więcej</strong> jego
            dni roboczych (np. 31.08–04.09 → wrzesień, 28.09–02.10 → wrzesień). Obrót miesiąca = suma obrotów tych tygodni z
            rozliczeń tygodniowych — ten sam silnik i te same reguły (dzień dostawy, zrealizowane, bez usuniętych).
            Marża TuSzamy netto: {data.rules.warsaw.city} {formatStawka(data.rules.warsaw.marginNet)}, pozostałe miasta{' '}
            {formatStawka(data.rules.otherCities.marginNet)} — w grupie każdy członek swoją stawką, potem suma.
            VAT {formatStawka(data.rules.commissionVatRate)} od marży netto; FV brutto = marża netto + VAT. Kwoty faktury do grosza
            (bez zaokrąglania do pełnych złotych). Ikona kopiowania wkleja kwotę jako „1234,56”.
          </Typography>
        </>
      )}

      {!isLoading && !error && !data && (
        <EmptyState title="Brak danych" description="Serwer nie zwrócił rozliczenia miesiąca." />
      )}
    </Stack>
  );
}
